import { useState, type ReactNode } from 'react';
import { zonedDateTimeToUtc, zonedParts } from '@nest-recruit/shared';
import { formatZoneName } from '../lib/format';
import { useNow } from '../lib/use-now';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';
import styles from './DateTimeField.module.css';

// R7 (QoL §14 "date and time, with the timezone abbreviation"): a day and a time on the wall
// clock of `timeZone`, handed back as one UTC instant. Our DatePicker and TimePicker, never
// `<input type="datetime-local">` (CLAUDE.md §5), and the zone always shown (non-negotiable #8).

interface DateTimeFieldProps {
  readonly label: string;
  /** ISO, or null for "no time yet". */
  readonly value: string | null;
  /** Called once both a day and a time are chosen. */
  readonly onChange: (iso: string) => void;
  readonly timeZone: string;
  readonly disabled?: boolean;
}

export function DateTimeField({ label, value, onChange, timeZone, disabled = false }: DateTimeFieldProps): ReactNode {
  const now = useNow();
  const initial = value ? zonedParts(value, timeZone) : { day: '', time: '' };
  const [day, setDay] = useState(initial.day);
  const [time, setTime] = useState(initial.time);
  const today = zonedParts(new Date(now).toISOString(), timeZone).day;
  const zone = formatZoneName(value ?? now, { timeZone });

  function change(nextDay: string, nextTime: string): void {
    setDay(nextDay);
    setTime(nextTime);
    const at = nextDay && nextTime ? zonedDateTimeToUtc(nextDay, nextTime, timeZone) : null;
    if (at) onChange(at.toISOString());
  }

  return (
    <div className={styles.field} role="group" aria-label={label}>
      <DatePicker label={`${label}: day`} value={day} min={today} onChange={(next) => change(next, time)} timeZone={timeZone} disabled={disabled} />
      <TimePicker label={`${label}: time`} value={time} onChange={(next) => change(day, next)} disabled={disabled} />
      <span className={styles.zone}>{zone}</span>
    </div>
  );
}
